import { Injectable } from '@angular/core';
import { HttpHeaders, HttpClient, HttpResponse } from '@angular/common/http';
import { environment } from '../../../environments/environment';
import { IAIBody, IAIElement, OpenAIRole } from 'src/app/models/chatAiMessage';
import { BehaviorSubject, Observable, throwError } from 'rxjs';
import { map, take } from 'rxjs/operators';
import { TranslateService } from '@ngx-translate/core';
import { TranslateConfigService } from '../translate/translate-config.service';
const { API_URL } = environment;

@Injectable({
  providedIn: 'root'
})
export class ChatAIService {
  private feed: BehaviorSubject<IAIBody>;
  private isWelcome: BehaviorSubject<boolean>;
  private lastPage: BehaviorSubject<string>;
  private programmaticRoute: BehaviorSubject<any>;

  public feed$: Observable<IAIBody>;
  public isWelcome$: Observable<boolean>;
  public lastPage$: Observable<string>;
  public programmaticRoute$: Observable<any>;

  constructor(
    private httpClient: HttpClient,
    private translateService: TranslateService,
    private translateConfigService: TranslateConfigService,
  ) {
    this.feed = new BehaviorSubject<IAIBody>({ elements: [] });
    this.isWelcome = new BehaviorSubject<boolean>(false);
    this.lastPage = new BehaviorSubject<string>('');
    this.programmaticRoute = new BehaviorSubject<any>(null);

    this.feed$ = this.feed.asObservable();
    this.isWelcome$ = this.isWelcome.asObservable();
    this.lastPage$ = this.lastPage.asObservable();
    this.programmaticRoute$ = this.programmaticRoute.asObservable();
  }

  private getCurrentFeedValue() {
    return this.feed.getValue();
  }
  
  private getCurrentRouteValue() {
    return this.programmaticRoute.getValue();
  }

  public pushStdMessage(element: IAIElement) {
    const currentFeed = this.getCurrentFeedValue();
    currentFeed.elements.push(element);

    this.feed.next(currentFeed);
  }

  public pushWelcomeMessage() {
    const welcomeMessage = this.translateService.instant('AI_CHAT_PROMPT');
    this.pushStdMessage({ content: welcomeMessage, role: OpenAIRole.system });

    this.isWelcome.next(true);
  }

  public pushRetryErrorMessage() {
    const errorMessage = this.translateService.instant('AI_CHAT_ERROR');
    this.pushStdMessage({ content: errorMessage, role: OpenAIRole.system, isRetry: true });

    this.isWelcome.next(true);
  }

  public composeSendRoute(token: string) {
    this.feed.pipe(
      take(1),
      map(it => ({ currentJSON: it.currentJSON, elements: it.elements.filter(it => !it.isLoading) })),
      map(it2 => ({ currentJSON: it2.currentJSON, elements: it2.elements.filter(it => !it.isRetry) })),
      map(it3 => ({ currentJSON: it3.currentJSON, elements: it3.elements.filter(it => !it.isConfirm) }))
    ).subscribe(sanitizedBody => {
      this.askToAI(token, sanitizedBody);
    });
  }

  public askToAI(token: string, body: IAIBody) {
    const headers = new HttpHeaders({ Authorization: `Bearer ${token}` });
    
    const currentLanguage = this.translateConfigService.getCurrentLanguage();
    body.language = currentLanguage;
    body.advanced = true;

    // const err = throwError(new Error('Ops!')); // DEBUG errors

    const data = this.httpClient.post<IAIBody>(`${API_URL}/ai`, body, { headers });
    data.subscribe({
      next: (body: IAIBody) => {
        if (body) {
          console.log('>>> body', body);
          const { currentJSON = {} } = body;
          
          const currentFeed = this.getCurrentFeedValue();
          const updatedFeed = { ...currentFeed };

          if (updatedFeed.elements && updatedFeed.elements.length > 0) {
            updatedFeed.elements = updatedFeed.elements.filter(element => !element.isLoading);
          }

          const lastElement = body.elements.filter((it) => it.role === OpenAIRole.assistant);
          if (lastElement.length > 0) {
            updatedFeed.elements.push(lastElement[lastElement.length - 1]);
          }

          updatedFeed.currentJSON = currentJSON;

          this.feed.next(updatedFeed);
        }
      },
      error: async (error) => { 
        console.log('error', error);
        const currentFeed = this.getCurrentFeedValue();
        const updatedFeed = { ...currentFeed };

        if (updatedFeed.elements && updatedFeed.elements.length > 0) {
          updatedFeed.elements = updatedFeed.elements.filter(it => !it.isLoading);

          const lastElement = updatedFeed.elements[updatedFeed.elements.length - 1];
          lastElement.isLastUserMessage = true;
        }

        this.feed.next(updatedFeed);
        
        this.pushRetryErrorMessage();
      },
      complete: () => {},
    });
  }

  public isReleased(): Promise<string> {
    return this.httpClient.get(`${API_URL}/ai/show-feature`, { responseType: 'text' }).toPromise();
  }
  
  public resetFinalJSON() {
    const currentFeed = this.getCurrentFeedValue();
    currentFeed.currentJSON = undefined;

    this.feed.next(currentFeed);
  }

  public resetChat() {
    const currentFeed = this.getCurrentFeedValue();
    currentFeed.elements = [];

    this.feed.next(currentFeed);
    this.isWelcome.next(false);
  }

  public resetRoute() {
    this.programmaticRoute.next(null);
  }

  public getData(): any {
    return this.getCurrentRouteValue();
  }

  public setData(sendRouteParams: any) {
    this.programmaticRoute.next(sendRouteParams);
  }

  public setPage(url: string) {
    this.lastPage.next(url);
  }
}
